import React, { useState, useEffect } from 'react';
import { UserPlus, UserCheck, Trash2, Eye, Mail, Phone, Star } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { engineers as allEngineers } from '../../mock';

const API = '/api';
const ADMIN_PASSWORD = 'Daquan22';

const EmployeeManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedEngineerId, setSelectedEngineerId] = useState('');

  // Stats cover every engineer (incl. inactive with history); dropdown only active ones
  const engineers = allEngineers;
  const activeEngineers = allEngineers.filter((e) => e.active !== false);

  useEffect(() => {
    fetchEmployees();
    fetchAllUsers();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      // Fetch all users and filter employees
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` }
      }).catch(() => ({ data: null }));
      
      // For now, we'll get employees through stats
      const employeePromises = engineers.map(eng => 
        axios.get(`${API}/employees/stats/${eng.id}`).catch(() => null)
      );
      
      const results = await Promise.all(employeePromises);
      const employeeData = results
        .filter(r => r && r.data)
        .map(r => r.data);
      
      setEmployees(employeeData);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      // This would need a backend endpoint to list all users
      // For now, we'll just have the form to add employees
      setAllUsers([]);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleCreateEmployee = async (e) => {
    e.preventDefault();
    
    if (!selectedUser || !selectedEngineerId) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      const response = await axios.post(`${API}/employees/setup`, {
        email: selectedUser,
        engineer_id: parseInt(selectedEngineerId),
        admin_password: ADMIN_PASSWORD
      });

      toast.success(response.data.message);
      setShowCreateModal(false);
      setSelectedUser('');
      setSelectedEngineerId('');
      fetchEmployees();
    } catch (error) {
      console.error('Error creating employee:', error);
      toast.error(error.response?.data?.detail || 'Failed to create employee');
    }
  };

  const handleRemoveEmployee = async (employeeId) => {
    if (!window.confirm('Are you sure you want to remove this employee access?')) {
      return;
    }

    try {
      // This would need a backend endpoint to remove employee status
      toast.info('Employee removal feature coming soon');
    } catch (error) {
      console.error('Error removing employee:', error);
      toast.error('Failed to remove employee');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Employee Management</h2>
          <p className="text-gray-400">Manage engineer accounts and access</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all flex items-center gap-2"
        >
          <UserPlus size={20} />
          Add Employee
        </button>
      </div>

      {/* Employee List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading ? (
          <div className="col-span-full text-center py-8 text-gray-400">
            Loading employees...
          </div>
        ) : employees.length > 0 ? (
          employees.map((employee) => (
            <div
              key={employee.engineer_id}
              data-testid={`employee-card-${employee.engineer_id}`}
              className="bg-gradient-to-br from-gray-900 to-black border border-purple-500/20 rounded-xl p-6 hover:border-purple-500/40 transition-all"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-purple-600/20 p-3 rounded-full">
                    <UserCheck className="text-purple-400" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">{employee.engineer_name}</h3>
                    <p className="text-gray-400 text-sm">Engineer ID: {employee.engineer_id}</p>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-purple-900/20 border border-purple-500/20 rounded-lg p-3">
                  <p className="text-gray-400 text-xs mb-1">Total Bookings</p>
                  <p className="text-white font-bold text-xl">{employee.total_bookings}</p>
                </div>
                <div className="bg-purple-900/20 border border-purple-500/20 rounded-lg p-3">
                  <p className="text-gray-400 text-xs mb-1">Total Hours</p>
                  <p data-testid={`employee-hours-${employee.engineer_id}`} className="text-white font-bold text-xl">{Math.round(employee.total_hours * 10) / 10}h</p>
                </div>
                <div className="bg-purple-900/20 border border-purple-500/20 rounded-lg p-3">
                  <p className="text-gray-400 text-xs mb-1">Collected</p>
                  <p data-testid={`employee-collected-${employee.engineer_id}`} className="text-white font-bold text-xl">${employee.total_revenue.toFixed(2)}</p>
                  <p className="text-gray-500 text-[11px] mt-0.5">Booked ${(employee.total_booked ?? 0).toFixed(2)}</p>
                </div>
                <div className="bg-purple-900/20 border border-purple-500/20 rounded-lg p-3">
                  <p className="text-gray-400 text-xs mb-1">Avg Rating</p>
                  <div className="flex items-center gap-1">
                    <Star className="text-yellow-400 fill-yellow-400" size={16} />
                    <p className="text-white font-bold text-xl">{employee.average_rating}</p>
                  </div>
                </div>
              </div>

              {/* Notification Settings */}
              <div className="bg-black/40 border border-purple-500/10 rounded-lg p-3 space-y-2">
                <p className="text-gray-400 text-xs font-semibold mb-2">Notification Preferences:</p>
                {employee.notification_email ? (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail size={14} className="text-purple-400" />
                    <span className="text-gray-300">{employee.notification_email}</span>
                  </div>
                ) : (
                  <p className="text-gray-500 text-xs">No email set</p>
                )}
                {employee.notification_phone ? (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone size={14} className="text-purple-400" />
                    <span className="text-gray-300">{employee.notification_phone}</span>
                  </div>
                ) : (
                  <p className="text-gray-500 text-xs">No phone set</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => window.location.href = `/employee?id=${employee.engineer_id}`}
                  className="flex-1 px-3 py-2 bg-purple-900/30 text-purple-300 rounded-lg text-sm font-semibold hover:bg-purple-900/50 transition-all flex items-center justify-center gap-2"
                >
                  <Eye size={16} />
                  View Dashboard
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12 bg-gray-900/50 border border-purple-500/20 rounded-xl">
            <UserPlus className="mx-auto text-gray-600 mb-4" size={48} />
            <p className="text-gray-400 mb-2">No employees yet</p>
            <p className="text-gray-500 text-sm">Click "Add Employee" to create an employee account</p>
          </div>
        )}
      </div>

      {/* Create Employee Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-purple-500/50 rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-4">Add Employee Account</h3>
            
            <form onSubmit={handleCreateEmployee} className="space-y-4">
              {/* Instructions */}
              <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-3">
                <p className="text-yellow-200 text-sm">
                  ⚠️ The user must have a registered account first before you can upgrade them to employee.
                </p>
              </div>

              {/* Email Input */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Employee Email
                </label>
                <input
                  type="email"
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  placeholder="engineer@hqstu.com"
                  required
                  className="w-full px-4 py-3 bg-black/40 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                />
                <p className="text-xs text-gray-500 mt-1">The email they used to register</p>
              </div>

              {/* Engineer Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Select Engineer
                </label>
                <select
                  value={selectedEngineerId}
                  onChange={(e) => setSelectedEngineerId(e.target.value)}
                  required
                  className="w-full px-4 py-3 bg-black/40 border border-purple-500/30 rounded-lg text-white focus:outline-none focus:border-purple-500"
                >
                  <option value="">Choose engineer...</option>
                  {activeEngineers.map(eng => (
                    <option key={eng.id} value={eng.id}>
                      {eng.name} ({eng.rate})
                    </option>
                  ))}
                </select>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setSelectedUser('');
                    setSelectedEngineerId('');
                  }}
                  className="flex-1 px-4 py-2 bg-gray-800 text-gray-300 rounded-lg font-semibold hover:bg-gray-700 transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all"
                >
                  Create Employee
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeManagement;
